import * as TableBasedTemplates from 'types/tableBasedTemplates';
import { BusinessAndRole, dashboard } from 'types';
import {
  ElmPartyType,
  ElmPurchaseParty,
  ElmWalkInParty,
  Party,
  PartyFromElm,
  ServiceTable,
  StateDescription,
  StateGroupingMap,
} from 'types/service';
import {
  FALLBACK_SERVICE_TYPE,
  checkHasUnreadTextMessage,
  getFullName,
  getNameForParty,
  getStateGroup,
  inferServiceTypeForPartyWithPurchase,
  inferServiceTypeForWalkInParty,
} from 'utils/partyUtils';
import { Features, core, safeEnumCompare } from '@tock/coretypes';
import { GuestData } from 'state/calendarCrm';
import { IServiceObject } from 'utils/getServiceDayDetails';
import { Instant, LocalDateTime, ZoneId } from 'js-joda';
import {
  PartiesGuestData,
  partiesGuestDataSelector,
} from 'selectors/service/partiesGuestDataSelector';
import { StoreType } from '@tock/shared/utils/connect';
import { assertNever, filterNilValues } from '@tock/shared/utils/typeUtils';
import { calendarEntitySelector } from 'selectors/calendarSelectors';
import { createOptionMap } from 'utils/purchaseUtils';
import { createSelector } from 'reselect';
import { elmTablesSelector } from './elmTablesSelector';
import { eventExperienceIdsSelector } from 'selectors/experienceSelectors';
import { featuresSelector, flexSelector } from 'selectors/featuresSelector';
import { findRelevantTurnTime } from 'utils/tableBasedTemplatesUtils';
import { getAmountDue } from '@tock/shared/utils/purchase';
import { getBusinessGuestProfile } from 'utils/guestProfileUtils';
import { getGuestIntegrationInformation } from 'utils/guestIntegrationsUtils';
import { getPhoneNumber } from '@tock/shared/utils/patron';
import { getSingleDisplayNameForTables } from 'utils/serviceTableUtils';
import { getTagsForProfile, isPriority } from 'utils/tagUtils';
import { oc } from 'ts-optchain';
import { partyToTableSelector } from './tableToPartySelector';
import { roundTo15MinInterval } from 'utils/timeUtils';
import { secondaryStatesSelector } from 'selectors/service/secondaryStatesSelector';
import { serverIdToPartyIdsSelector } from 'selectors/serverIdToPartyIdsSelector';
import { serviceDayTurnTimesBySizeSelector } from 'selectors/routes/table-based-templates/turnTimesBySizeSelector';
import { timeZoneSelector } from 'selectors/timeZoneSelector';
import _ from 'lodash';
import currentBusinessSelector from 'selectors/service/currentBusinessSelector';
import priorityTagsSelector from 'selectors/priorityTagsSelector';
import protos from 'utils/protos';
import seatedTablesSelector from 'selectors/service/seatedTablesSelector';
import serviceObjectsForFilteringByDaySelector from 'selectors/service/serviceObjectsForFilteringByDaySelector';

const { NoteType, Tag } = protos;

interface WalkInPreParty {
  type: 'WalkIn';
}

interface AssociatedWalkInPreParty {
  type: 'AssociatedWalkIn';
  profile?: dashboard.GuestProfile;
}

interface PurchasePreParty {
  type: 'Purchase';
  profile?: dashboard.GuestProfile;
  purchase?: core.PastPurchase;
}

type PreParty = WalkInPreParty | AssociatedWalkInPreParty | PurchasePreParty;

function getServerIdsForPartyId(
  partyId: number,
  serverIdToPartyIds: Dictionary<number[]>
): string[] {
  return Object.keys(serverIdToPartyIds).reduce((acc: string[], serverId: string) => {
    const partyIdsForServer = serverIdToPartyIds[serverId];
    let partyIdForServer;
    if (_.size(partyIdsForServer) > 0) {
      partyIdForServer = partyIdsForServer.find((partyIdForServer) => partyIdForServer === partyId);
    }
    if (partyIdForServer) {
      acc.push(serverId);
    }
    return acc;
  }, []);
}

/**
 * yes, thats right, I named it that :)
 * git blame to find out who
 */
function getPreParty({
  guestData,
  party,
  purchaseMap,
}: {
  guestData: NumericDictionary<GuestData>;
  party: PartyFromElm;
  purchaseMap: StoreType['calendar']['purchases'];
}): PreParty {
  const associatedPatronId = party.patronInfo?.id;
  const guestDataFromMap = associatedPatronId ? guestData[associatedPatronId] : undefined;

  const profile = guestDataFromMap
    ? {
        ...guestDataFromMap.guestProfile,
        currentBusinessGuestProfile: guestDataFromMap.businessGuestProfile,
      }
    : undefined;

  if (party.partyType.partyType === 'PurchaseParty') {
    const purchaseFromMap: core.PastPurchase | undefined = party.partyType.originalPurchaseId
      ? purchaseMap[party.partyType.originalPurchaseId]
      : undefined;
    return {
      type: 'Purchase',
      purchase: purchaseFromMap,
      profile: profile,
    };
  }

  if (associatedPatronId) {
    return {
      type: 'AssociatedWalkIn',
      profile: profile,
    };
  }

  return {
    type: 'WalkIn',
  };
}

/**
 * A large party is a reservation where the party size is at or above the configured minLargePartySize
 * AND not a walk in or an event experience
 *
 * exported for testing
 */
export function getIsLargeParty(
  party: PartyFromElm,
  partySize: number,
  eventExperienceIds: number[],
  currentBusiness?: BusinessAndRole
): boolean {
  if (
    !currentBusiness ||
    !currentBusiness.distinguishLargeParties ||
    !currentBusiness.minLargePartySize
  ) {
    return false;
  }

  if (party.experienceId && eventExperienceIds.includes(party.experienceId)) {
    return false;
  }

  return partySize >= currentBusiness.minLargePartySize;
}

/**
 * Bundles time zone and current business to compose with party selector
 * This is here because the types for re-select allow 2-13 params in createSelector
 */
const configSelector = createSelector(
  timeZoneSelector,
  currentBusinessSelector,
  flexSelector,
  (timeZone, currentBusiness, isFlex) => ({
    currentBusiness,
    timeZone,
    isFlex,
  })
);

function computeParties(
  {
    timeZone,
    currentBusiness,
    isFlex,
  }: { isFlex: boolean; timeZone: string; currentBusiness?: BusinessAndRole },
  partiesGuestData: PartiesGuestData,
  partyIdToTableId: Dictionary<number[]>,
  tablesDict: NumericDictionary<ServiceTable>,
  groupedSecondaryStates: StateGroupingMap,
  priorityBusinessTags: dashboard.Tag[],
  serviceObjectsByDay: IServiceObject[] | undefined,
  numSeatedPartiesAtTable: Dictionary<number>,
  calendarEntity: {
    purchases: StoreType['calendar']['purchases'];
    visitsByWalkinId: StoreType['calendar']['visitsByWalkinId'];
  },
  eventExperienceIds: number[],
  serverIdToPartyIds: Dictionary<number[]> | undefined,
  turnTimes: TableBasedTemplates.TurnTimes,
  features: Features
): Party[] {
  const { parties, guestData } = partiesGuestData;
  const { purchases: originalPurchaseToPurchaseMap, visitsByWalkinId } = calendarEntity;
  const priorityTags: Dictionary<Dictionary<dashboard.Tag>> = {};
  _.forEach(priorityBusinessTags, (tag) => {
    priorityTags[tag.type] = priorityTags[tag.type] || {};
    priorityTags[tag.type][tag.text] = tag;
  });

  return _.map(parties, (party: PartyFromElm): Party => {
    const prePartyData = getPreParty({
      guestData,
      party,
      purchaseMap: originalPurchaseToPurchaseMap,
    });

    const serverIdsForParty = serverIdToPartyIds
      ? getServerIdsForPartyId(party.id, serverIdToPartyIds)
      : undefined;

    const tableIds = oc(partyIdToTableId)[party.id]([]);
    let tables: ServiceTable[] = [];

    if (_.size(tableIds) > 0) {
      tables = tableIds.map((tableId) => tablesDict && tablesDict[tableId]).filter(filterNilValues);
      tables = _.sortBy(tables, 'name');
    }

    const tableString = getSingleDisplayNameForTables(tables);

    const tableCapacity = _.reduce(
      tables,
      (acc, table) => {
        return acc + table.size;
      },
      0
    );

    const partyTablesInUse = _.some(tableIds, (table) => numSeatedPartiesAtTable[table] >= 1);

    let options: core.PurchasedOption[] = [];
    if (prePartyData.type === 'Purchase' && prePartyData.purchase) {
      options = (prePartyData.purchase.purchasedOption || []) as core.PurchasedOption[];
    }
    const groupedOptions = createOptionMap(options);

    let requiresPayment = false;
    if (prePartyData.type === 'Purchase' && prePartyData.purchase) {
      requiresPayment = getAmountDue(prePartyData.purchase) > 0;
    }

    let partySize = party.numPeoplePresent;
    const nulledSecondaryState = party.secondaryState || null;
    const stateList = groupedSecondaryStates[getStateGroup(party.state)];

    let stateDescription!: StateDescription;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const matchingStateDescription = _.find(
      stateList as StateDescription[],
      (secondaryState: StateDescription) => {
        return (
          nulledSecondaryState === secondaryState.secondaryValue &&
          party.state === secondaryState.primaryState
        );
      }
    )!;

    if (matchingStateDescription) {
      stateDescription = {
        ...matchingStateDescription,
      };
    }

    // Small changes to do stuff with
    if (!stateDescription) {
      const fallBackStateDescription =
        _.find(stateList, (secondaryState) => {
          return secondaryState.secondaryValue === null;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }) || ({} as any);
      stateDescription = {
        ...fallBackStateDescription,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        stateName: party.secondaryState!,
      };
    }

    // 2019/05/01 KM: this returns 2 different types. its casting here because
    // its not sending scope, so it's returning the businesss guest profile.
    // should we send the scope to this? its used in other places, but not here and not
    // in the original method used for this.
    let currentBusinessGuestProfile: dashboard.BusinessGuestProfile | undefined;
    if (
      (prePartyData.type === 'Purchase' || prePartyData.type === 'AssociatedWalkIn') &&
      prePartyData.profile &&
      currentBusiness
    ) {
      currentBusinessGuestProfile = getBusinessGuestProfile(
        prePartyData.profile,
        currentBusiness.id
      ) as dashboard.BusinessGuestProfile | undefined;
    }

    let visit = {};
    if (prePartyData.type === 'Purchase' && prePartyData.purchase && prePartyData.purchase.visit) {
      visit = prePartyData.purchase.visit as core.Visit;
    } else if (prePartyData.type === 'AssociatedWalkIn' || prePartyData.type === 'WalkIn') {
      visit = visitsByWalkinId[party.id];
    }

    const visitTags = oc(visit as core.Visit).tag([]);

    let guestTags: string[] = [];
    if (
      (prePartyData.type === 'Purchase' || prePartyData.type === 'AssociatedWalkIn') &&
      prePartyData.profile
    ) {
      guestTags = getTagsForProfile(prePartyData.profile);
    }

    const isVisitPriority = isPriority(visitTags, Tag.TagType.VISIT, priorityTags);
    const isGuestPriority = isPriority(guestTags, Tag.TagType.GUEST, priorityTags);
    const hasUnreadTextMessage = checkHasUnreadTextMessage(party.textMessage);

    const attributes =
      prePartyData.type === 'WalkIn'
        ? null
        : oc(prePartyData.profile as dashboard.GuestProfile).attribute();
    const isWineDirectMember =
      !!attributes &&
      !_.isEmpty(getGuestIntegrationInformation('WINEDIRECT_CLASSIC', attributes).membership);

    const isSalesforceMember =
      !!attributes &&
      !_.isEmpty(getGuestIntegrationInformation('SALESFORCE', attributes).membership);
    const isBloyalMember =
      !!attributes && !_.isEmpty(getGuestIntegrationInformation('BLOYAL', attributes).membership);

    const currentBusinessNotes = oc(currentBusinessGuestProfile).note([]);

    let businessGroupNotes: dashboard.AuditedNote[] = [];
    if (
      (prePartyData.type === 'Purchase' || prePartyData.type === 'AssociatedWalkIn') &&
      prePartyData.profile
    ) {
      businessGroupNotes = oc(prePartyData.profile).businessGroupGuestProfile.note(
        []
      ) as dashboard.AuditedNote[];
    }
    let hasPatronNotes = false;
    let hasDietaryNotes = false;
    // YK (2017/04/14): I'm not too concerned about performance here because each note list is at most length 2
    _.forEach([currentBusinessNotes, businessGroupNotes], (noteList) => {
      _.forEach(noteList, (auditedNote) => {
        if (!auditedNote.note) {
          return;
        }
        if (safeEnumCompare(auditedNote.noteType, NoteType, NoteType.GENERAL)) {
          hasPatronNotes = true;
        }
        if (safeEnumCompare(auditedNote.noteType, NoteType, NoteType.DIETARY)) {
          hasDietaryNotes = true;
        }
      });
    });

    let serviceTypeString = null;
    if (prePartyData.type === 'WalkIn' || prePartyData.type === 'AssociatedWalkIn') {
      serviceTypeString = inferServiceTypeForWalkInParty(
        {
          arrivalTime: party.arrivalTime,
          expectedReservationTime: party.expectedReservationTime || undefined,
          quotedTimeMinutes: party.quotedTimeMinutes,
          seatedTime: party.seatedTime,
          walkInCreatedTimeSec: party.walkInCreatedTimeSec,
        },
        serviceObjectsByDay,
        timeZone
      );
    } else if (prePartyData.type === 'Purchase') {
      // For old service data with missing party size, fall back to party size from purchase.
      if (oc(prePartyData).purchase.ticketCount()) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        partySize = partySize || oc(prePartyData).purchase.ticketCount()!;
      }
      // For a purchase, start with the expected reservation time
      // Then fall back to the purchase's ticketDateTime
      // and ultimately to the service type bit if necessary
      serviceTypeString = inferServiceTypeForPartyWithPurchase(
        party,
        prePartyData.purchase,
        serviceObjectsByDay
      );
    } else {
      serviceTypeString = FALLBACK_SERVICE_TYPE;
    }

    let patron;
    if (prePartyData.type === 'AssociatedWalkIn' || prePartyData.type === 'Purchase') {
      if (isFlex) {
        patron = oc(prePartyData).profile.patron() ||
          oc(party).patronInfo() || {
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            phoneCountryCode: '',
          };
      } else {
        patron = {
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          phoneCountryCode: '',
          ...oc(prePartyData).profile.patron(),
          ...oc(party).patronInfo(),
        };
      }
    } else {
      patron = oc(party).patronInfo({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        phoneCountryCode: '',
      });
    }

    const name = getFullName({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      firstName: patron.firstName!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      lastName: patron.lastName!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      email: patron.email!,
    });
    const lastNameFirstName = getNameForParty({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      firstName: patron.firstName!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      lastName: patron.lastName!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      email: patron.email!,
    });

    const isLargeParty = getIsLargeParty(party, partySize, eventExperienceIds, currentBusiness);

    const partyBase = {
      groupedOptions,
      hasDietaryNotes,
      hasPatronNotes,
      hasUnreadTextMessage,
      isLargeParty,
      isGuestPriority,
      isVisitPriority,
      lastNameFirstName,
      name,
      numPeoplePresent: partySize,
      partySize,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      phone: getPhoneNumber(oc(patron).phoneCountryCode(), oc(patron).phone())!,
      phoneDetails: {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        phone: oc(patron).phone()!,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        phoneCountryCode: oc(patron).phoneCountryCode()!,
      },
      requiresPayment,
      secondaryState: nulledSecondaryState || undefined, // Replace undefined with null if needed
      serverIds: serverIdsForParty,
      serviceTypeString,
      stateDescription,
      tableCapacity,
      tableIds,
      tables,
      tableString,
      partyTablesInUse,
      isWineDirectMember,
      isSalesforceMember,
      isBloyalMember,
      expectedTurnTimeMinutes: getExpectedTurnTimeMinutes(turnTimes, party, timeZone),
    };

    // [Blake 5/23/2020] Jumping through some hoops here and below to get typescript to recognize correct types
    const partyTypeAndPreParty = getPartyTypeAndPreParty(prePartyData, party.partyType);

    switch (partyTypeAndPreParty.type) {
      case 'Purchase': {
        return {
          ...party,
          ...partyBase,
          currentBusinessGuestProfile,
          primaryDinerProfile: partyTypeAndPreParty.prePartyData.profile,
          visit: partyTypeAndPreParty.prePartyData.purchase?.visit as core.Visit,
          purchase: partyTypeAndPreParty.prePartyData.purchase,
          partyType: partyTypeAndPreParty.partyType,
          type: 'Booking',
          listItemType: 'Party',
        };
      }
      case 'AssociatedWalkIn': {
        return {
          ...party,
          ...partyBase,
          currentBusinessGuestProfile,
          primaryDinerProfile: partyTypeAndPreParty.prePartyData.profile,
          visit: visitsByWalkinId[party.id],
          partyType: partyTypeAndPreParty.partyType,
          type: 'WalkIn',
          walkInType: 'Associated',
          listItemType: 'Party',
        };
      }
      case 'WalkIn': {
        return {
          ...party,
          ...partyBase,
          visit: visitsByWalkinId[party.id],
          partyType: partyTypeAndPreParty.partyType,
          type: 'WalkIn',
          listItemType: 'Party',
          walkInType: 'WalkIn',
        };
      }

      default:
        assertNever(partyTypeAndPreParty);
    }
  });
}

export default createSelector(
  configSelector,
  partiesGuestDataSelector,
  partyToTableSelector,
  elmTablesSelector,
  secondaryStatesSelector,
  priorityTagsSelector,
  serviceObjectsForFilteringByDaySelector,
  seatedTablesSelector,
  calendarEntitySelector,
  eventExperienceIdsSelector,
  serverIdToPartyIdsSelector,
  serviceDayTurnTimesBySizeSelector,
  featuresSelector,
  computeParties
);

const getExpectedTurnTimeMinutes = (
  turnTimes: TableBasedTemplates.TurnTimes,
  party: PartyFromElm,
  timeZone: string
) => {
  const partyStartDateTime = getPartyStartTime(party, timeZone);

  return partyStartDateTime
    ? findRelevantTurnTime(turnTimes, {
        partySize: party.expectedPartySize,
        startDateTime: partyStartDateTime,
        ticketTypeId: party.experienceId,
      })?.expectedTurnTimeMinutes
    : undefined;
};

const getPartyStartTime = (party: PartyFromElm, timeZone: string) => {
  if (party.partyType.partyType === 'PurchaseParty' && party.expectedReservationTime) {
    return LocalDateTime.parse(party.expectedReservationTime);
  }
  if (party.partyType.partyType === 'WalkInParty' && party.seatedTime) {
    return roundTo15MinInterval(
      LocalDateTime.ofInstant(Instant.ofEpochSecond(party.seatedTime), ZoneId.of(timeZone))
    );
  }
  return;
};

const getPartyTypeAndPreParty = (
  prePartyData: PreParty,
  partyType: ElmPartyType
): PartyTypeAndPreParty => {
  switch (prePartyData.type) {
    case 'Purchase': {
      if (partyType.partyType === 'PurchaseParty') {
        return { type: 'Purchase', prePartyData, partyType };
      } else {
        throw new Error('Invalid party state detected');
      }
    }
    case 'AssociatedWalkIn': {
      if (partyType.partyType === 'PurchaseParty') {
        throw new Error('Invalid party state detected');
      } else {
        return { type: 'AssociatedWalkIn', prePartyData, partyType };
      }
    }
    case 'WalkIn': {
      if (partyType.partyType === 'PurchaseParty') {
        throw new Error('Invalid party state detected');
      } else {
        return { type: 'WalkIn', prePartyData, partyType };
      }
    }
    default:
      assertNever(prePartyData);
  }
};

interface PartyTypeAndPrePartyPurchase {
  type: 'Purchase';
  prePartyData: PurchasePreParty;
  partyType: ElmPurchaseParty;
}

interface PartyTypeAndPrePartyAssociatedWalkin {
  type: 'AssociatedWalkIn';
  prePartyData: AssociatedWalkInPreParty;
  partyType: ElmWalkInParty;
}

interface PartyTypeAndPrePartyWalkIn {
  type: 'WalkIn';
  prePartyData: WalkInPreParty;
  partyType: ElmWalkInParty;
}

type PartyTypeAndPreParty =
  | PartyTypeAndPrePartyAssociatedWalkin
  | PartyTypeAndPrePartyPurchase
  | PartyTypeAndPrePartyWalkIn;
